/**
 * Document utility functions for working with documents in selection contexts.
 */

import { CORRESPONDENCE_FOLDER_NAME, CORRESPONDENCE_FOLDER_PATH } from '@/constants/document-constants';
import type { Document } from '@/hooks/useDocuments';
import type { Folder, FolderTree } from '@/hooks/useFolders';

/**
 * Flattened folder with additional display properties for use in select dropdowns.
 */
export interface FlattenedFolder extends Folder {
  displayName: string;
  hasChildren: boolean;
  depth: number;
  original: FolderTree;
}

/**
 * Check if a folder is the root system Correspondence folder.
 */
export const isCorrespondenceFolder = (folder: Folder): boolean =>
  folder.name?.toLowerCase() === CORRESPONDENCE_FOLDER_NAME.toLowerCase() &&
  folder.parent_folder_id == null &&
  folder.depth === 0 &&
  folder.path?.toLowerCase() === CORRESPONDENCE_FOLDER_PATH;

/**
 * Validate a name for a folder about to be created under `parentFolder` (root when omitted).
 * Returns the reason it can't be used, or null when the name is fine.
 */
export const newFolderNameError = (name: string, parentFolder?: Folder): string | null => {
  const trimmed = name.trim();
  if (!trimmed) {
    return 'Please enter a folder name';
  }
  if (!parentFolder && trimmed.toLowerCase() === CORRESPONDENCE_FOLDER_NAME.toLowerCase()) {
    return `Cannot create a root folder named "${CORRESPONDENCE_FOLDER_NAME}" - this is a system folder`;
  }
  return null;
};

/**
 * Check if a folder is the root system Correspondence folder or one of its descendants.
 */
export const isInCorrespondenceFolderTree = (folder: Folder): boolean =>
  folder.path?.toLowerCase().startsWith(CORRESPONDENCE_FOLDER_PATH) ?? false;

/**
 * Flatten a hierarchical folder tree into a flat array for use in select dropdowns.
 * Optionally filters folders using a predicate function.
 *
 * @param folders - Array of folders to flatten
 * @param depth - Current depth in the tree (defaults to 0)
 * @param filterFn - Optional filter function. Return false to exclude a folder and its children.
 * @returns Flat array of folders with depth and display properties
 */
export function flattenFolderTree(
  folders: FolderTree[],
  depth: number = 0,
  filterFn?: (folder: FolderTree) => boolean
): FlattenedFolder[] {
  const flattened: FlattenedFolder[] = [];

  for (const folder of folders) {
    // Apply filter if provided
    if (filterFn && !filterFn(folder)) {
      continue;
    }

    const hasChildren = folder.children && folder.children.length > 0;
    flattened.push({
      ...folder,
      displayName: folder.name,
      hasChildren,
      depth,
      original: folder,
    });

    if (hasChildren) {
      flattened.push(...flattenFolderTree(folder.children, depth + 1, filterFn));
    }
  }

  return flattened;
}

/** Primary folder id from correspondence row (`document_type.id` may be number or numeric string). */
export function parseDocumentFolderId(doc: Document): number | null {
  const raw = doc.document_type?.id as string | number | undefined;
  if (raw === undefined || raw === null || raw === '') {
    return null;
  }
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** True when doc's folder reference matches target folders by numeric id or guid. */
export function isDocumentInFolders(
  doc: Document,
  folderIds: Set<number>,
  folderGuids?: Set<string>,
): boolean {
  const fid = parseDocumentFolderId(doc);
  if (fid !== null && folderIds.has(fid)) return true;
  const fguid = doc.document_type?.guid;
  if (fguid && folderGuids?.has(fguid)) return true;
  return false;
}

/** Backend-aligned eligibility for ECA/matter memory counting. */
export function isCompletedNonDraftDocument(doc: Document): boolean {
  return doc.processing_status === 'completed' && !doc.is_draft;
}

export function collectDocumentGuidsInFolders(
  documents: Document[],
  folderIds: Set<number>,
  folderGuids?: Set<string>,
): Set<string> {
  const guids = new Set<string>();
  for (const d of documents) {
    if (!isCompletedNonDraftDocument(d)) continue;
    if (!isDocumentInFolders(d, folderIds, folderGuids)) continue;
    guids.add(d.guid);
  }
  return guids;
}

export function countDocumentsInFolders(
  documents: Document[],
  folderIds: Set<number>,
  folderGuids?: Set<string>,
): number {
  return collectDocumentGuidsInFolders(documents, folderIds, folderGuids).size;
}

/** `file:${fileReferenceGuid}` keys for selection ids under the target folders. */
export function collectFileReferenceIdsInFolders(
  documents: Document[],
  folderIds: Set<number>,
  folderGuids?: Set<string>,
): Set<string> {
  const ids = new Set<string>();
  for (const d of documents) {
    if (!isCompletedNonDraftDocument(d)) continue;
    if (!isDocumentInFolders(d, folderIds, folderGuids)) continue;
    ids.add(`file:${d.id}`);
  }
  return ids;
}
